<template>
  <div class="content-wrapper">
    <section class="content-header">
      <h1>
        User Setup
      </h1>
      <ol class="breadcrumb">
        <li>
          <router-link :to="{ name: 'dashboard' }"><i class="fa fa-dashboard"></i> Home</router-link>
        </li>
        <li>
          <router-link :to="{name: 'user-setup-page'}">User Setup</router-link>
        </li>
        <li class="active">
          <span v-if="isCreate">Create</span>
          <span v-else>Edit</span>
        </li>
      </ol>
    </section>
    
    <section class="content">
      <div class="row">
        <div class="col-md-12">
          <div class="box box-info">
            <div class="box-header with-border">
              <h3 class="box-title">{{ title }}</h3>
            </div>

            <form class="form-horizontal" @submit.prevent="submit">
              <div class="box-body" style="overflow: unset;">
                <div v-if="error" class="alert alert-danger">
                  <a class="close"
                    @click="error = false">
                    &times;
                  </a>
                  {{ error }}
                </div>

                <div class="form-group">
                  <label for="name"
                    class="col-sm-2 control-label">
                    Full Name <span class="req">*</span>
                  </label>

                  <div class="col-sm-10">
                    <input type="text"
                      class="form-control"
                      id="name"
                      placeholder="Name"
                      v-model="form.name">
                  </div>
                </div>

                <div class="form-group">
                  <label for="email"
                    class="col-sm-2 control-label">
                    Email <span class="req">*</span>
                  </label>

                  <div class="col-sm-10">
                    <input type="text"
                      class="form-control"
                      id="email"
                      placeholder="Email"
                      v-model="form.email"
                      :disabled="!isCreate">
                  </div>
                </div>

                <div class="form-group">
                  <label for="role" 
                    class="col-sm-2 control-label">
                    Role <span class="req">*</span>
                  </label>

                  <div class="col-sm-10">
                    <v-select
                      id="role"
                      name="role"
                      placeholder="Select Role"
                      v-model="form.role"
                      :options="list.roles">
                    </v-select>
                  </div>
                </div>
              </div>

              <div class="box-footer">
                <button type="submit" class="btn btn-info pull-right" v-if="isCreate">Create</button>
                <button type="submit" class="btn btn-info pull-right" v-else>Update</button>
                <a class="btn btn-default" @click.prevent="cancel">Cancel</a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Common from '@/mixins/Common';

export default {
  name: 'UserForm',
  mixins: [Common],
  props: {
    isCreate: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      title: 'Add New User',
      error: null,
      form: {
        name: null,
        email: null,
        role: null,
      },
      list: {
        roles: [],
      },
    };
  },
  created() {
    this.init();
  },
  methods: {
    init() {
      if (!this.isCreate) {
        this.title = 'Edit User';
        this.fetchUserDetails();
      }
      this.fetchRoles();
    },

    async fetchUserDetails() {
      const id = this.$route.params.id;
      try {
        const response = await this.axios.get(`/users/${id}`);
        const { data } = response.data;
        this.setFieldValue(data);
      } catch (error) {
        console.log(error);
        this.failedModal('fetch');
      }
    },

    setFieldValue(data) {
      this.form.name = data.name ? data.name : '';
      this.form.email = data.email ? data.email : '';
      this.form.role = {
        value: data.role.code,
        label: data.role.name,
      };
    },

    fetchRoles() {
      return new Promise(async (resolve, reject) => {
        try {
          const response = await this.axios.get('/roles?group=mister-aladin-staff');
          const { data } = response.data;
          data.forEach((role) => {
            this.list.roles.push({
              label: role.name,
              value: role.slug,
            });
          });
          resolve(data);
        } catch (error) {
          reject(error);
          this.errorModal();
        }
      });
    },

    submit() {
      const result = this.validate();
      if (!result) return;

      if (this.isCreate) {
        this.createUser();
      } else {
        this.updateUser();
      }
    },

    validate() {
      if (!this.form.name) {
        this.$swal('Name required', 'Please fill name to continue', 'warning');
        return false;
      }

      if (!this.form.email) {
        this.$swal('Email required', 'Please fill email to continue', 'warning');
        return false;
      }

      if (!this.form.role || this.form.role.length < 1) {
        this.$swal('Role required', 'Please select role to continue', 'warning');
        return false;
      }

      return true;
    },

    async createUser() {
      const data = this.generateData();
      try {
        await this.axios.post('/users', data);
        this.successModal('created', data.get('name'));
        this.cancel();
      } catch (error) {
        this.failedModal('create');
      }
    },

    async updateUser() {
      const data = this.generateData();
      const id = this.$route.params.id;
      try {
        await this.axios.put(`/users/${id}`, data);
        this.successModal('updated', data.get('name'));
        this.cancel();
      } catch (error) {
        this.failedModal('update');
      }
    },

    generateData() {
      const formData = new FormData();

      formData.append('name', this.form.name);
      formData.append('email', this.form.email);
      formData.append('role', this.form.role.value);

      return formData;
    },

    successModal(type, name) {
      this.$swal({
        title: this.capitalizeFirstLetter(type),
        text: `${name} has been ${type}.`,
        type: 'success',
        timer: 3000,
      });
    },

    failedModal(type) {
      this.$swal({
        title: `${this.capitalizeFirstLetter(type)} Failed!`,
        text: `Failed to ${type} user`,
        type: 'warning',
        timer: 3000,
      });
    },

    cancel() {
      this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss" scoped>
</style>


// WEBPACK FOOTER //
// src/components/modules/user/form.vue